import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import {
  LikeOutlineIcon,
  CheckCircleActiveIcon,
  RefreshIcon,
  MoreIcon,
  RenameIcon,
  DownloadIcon,
  TrashIcon,
  WarningRedIcon,
} from "@/components/ui/icon";
import { IconButton } from "@/components/common/icon-button";
import { Image } from "@/components/common/image";
import { MoreActionItem, MoreActionPopover } from "@/components/common/more-action-popover";
import { RenameModal } from "@/components/common/rename-modal";
import { VideoPlayer } from "@/components/common/video-player";
import { TruncatedText } from "@/components/common/truncated-text";
import { DeleteConfirmModal, Spinner } from "@/components/ui/modal";
import type { CardProps } from "./actor-types";
import unusual_cover from '@/assets/actor/unusual_cover.webp';

const Card: React.FC<CardProps> = ({
  className = "",
  name,
  image,
  video,
  hdVideo,
  isPro = false,
  isHD = false,
  isMyActor = false,
  isSelected = false,
  isLike = false,
  generateType,
  status = 2,
  isUnusual = false,
  onSelect,
  onLike,
  onRename,
  onDownload,
  onDelete,
  onRegenerate,
}) => {
  const { t } = useTranslation();

  const isSuccess = status === 2;
  const isProcessing = status < 2;
  const isFailed = status > 2;

  if (isUnusual) {
    return (
      <a href="mailto:support@bizdesk.ai" target="_blank">
        <div
          className={cn(
            "group relative rounded-sm overflow-hidden",
            "p-0.5 hover:shadow-md hover:border-primary/20 transition-all duration-200",
            "flex flex-col items-center text-center cursor-pointer",
            className
          )}
        >
          <Image src={image || unusual_cover} className="w-full h-full rounded-sm object-cover" />
          <div className={cn(
            "absolute -inset-0.5 rounded-sm flex flex-col items-center justify-center px-1 space-y-2 ",
            image && "bg-black/30 backdrop-blur-sm"
          )}>
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/30 backdrop-filter backdrop-blur-lg">
              <WarningRedIcon className="w-4 h-4 text-white" />
            </div>
            <span className="text-white text-xs font-medium underline">
              {t("modal_actors.actor_card.unusual_message")}
            </span>
          </div>
          <div
            className="opacity-0 group-hover:opacity-100 absolute top-2 right-2 transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <DeleteConfirmModal
              trigger={
                <IconButton
                  className="hover:bg-white/20"
                  icon={<TrashIcon className="w-4 h-4 text-white" />}
                />
              }
              onConfirm={() => onDelete()}
            />
          </div>
        </div>
        <div className="w-full text-start mt-2">
          <TruncatedText
            text={name}
            className="text-i-normal text-sm font-medium user-select-none"
          />
        </div>
      </a>
    );
  }
  return (
    <div
      className={`group relative select-none cursor-pointer transition-all duration-150 ${className}`}
      onClick={() => isSuccess && onSelect()}
    >
      {/* 头像容器 */}
      <div className="relative">
        {/* 头像图片 */}
        <div className="relative mb-3">
          <div
            className={cn(
              "p-0.5 rounded-sm transition-all duration-150",
              isSelected
                ? "outline outline-2 outline-i-normal"
                : "outline outline-transparent"
            )}
          >
            <VideoPlayer
              className="rounded-sm w-full aspect-[9/16] object-cover"
              showControlsBar={false}
              src={video}
              poster={image}
            />
          </div>
          {/* 选中状态 */}
          {isSuccess && (
            <div
              className={cn(
                "absolute top-[0.5625rem] left-[0.5625rem] opacity-0 transition-all duration-150",
                isSelected && "opacity-100"
              )}
            >
              <CheckCircleActiveIcon className="w-[1.125rem] h-[1.125rem]" />
            </div>
          )}
          {/** 收藏状态 */}
          {isSuccess && (
            <div
              className={cn(
                "absolute bottom-[0.5625rem] right-[0.5625rem] opacity-0 group-hover:opacity-100 transition-all duration-150",
                isLike && "opacity-100"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onLike();
              }}
            >
              <LikeOutlineIcon
                className={cn("w-5 h-5", isLike && "fill-[rgba(247,66,66,1)]")}
              />
            </div>
          )}
          {/* 生成中状态覆盖层 */}
          {isProcessing && (
            <div className="absolute inset-0.5 bg-black/40 rounded-sm">
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <Spinner className="w-6 h-6 text-white" />
                <p className="px-1 text-white font-medium text-xs">
                  {generateType === 2
                    ? t("modal_actors.actor_card.processing_message_image")
                    : t("modal_actors.actor_card.processing_message_video")}
                </p>
              </div>
            </div>
          )}

          {/* 失败状态覆盖层 */}
          {isFailed && (
            <div
              className="absolute inset-0.5 bg-black/60 rounded-sm flex flex-col items-center justify-center"
              onClick={() => onRegenerate()}
            >
              {/* 重新生成按钮 */}
              <button className="flex flex-col items-center justify-center px-1 gap-2">
                <RefreshIcon className="w-8 h-8" />
                <span className="text-i-white text-xs font-medium">
                  {t("modal_actors.actor_card.failed_message")}
                </span>
              </button>
            </div>
          )}

          {isMyActor && <div
            className="opacity-0 group-hover:opacity-100 absolute top-2 right-2"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreActionPopover
              className="w-40"
              side="bottom"
              tiggerIcon={
                <MoreIcon className="w-5 h-5 text-white hover:text-white" />
              }
              tiggerClassName="hover:bg-[rgba(0,0,0,0.2)]"
            >
              <RenameModal
                currentName={name}
                trigger={
                  <MoreActionItem
                    icon={<RenameIcon className="w-4 h-4" />}
                    label={t("common.rename")}
                  />
                }
                onConfirm={(name) => onRename(name)}
              />
              <MoreActionItem
                icon={<DownloadIcon className="w-5 h-5" />}
                label={t("common.download")}
                disabled={isProcessing || isFailed || !hdVideo}
                onClick={() => onDownload()}
              />
              <DeleteConfirmModal
                trigger={
                  <MoreActionItem
                    icon={<TrashIcon className="w-4 h-4" />}
                    label={t("common.delete")}
                    disabled={isProcessing || isFailed}
                  />
                }
                onConfirm={() => onDelete()}
              />
            </MoreActionPopover>
          </div>}
        </div>
        <div className="flex flex-start items-center gap-2">
          <TruncatedText
            className="text-i-normal text-sm font-medium user-select-none"
            text={name}
          />
          {/* Pro标签 */}
          {isPro && (
            <div className="shrink-0 px-1 py-0.5 text-[rgb(89,37,220)] text-xs font-semibold rounded bg-gray-100">
              {t("modal_actors.actor_card.pro")}
            </div>
          )}
          {/* HD标签 */}
          {isHD && (
            <div className="shrink-0 px-1 py-0.5 text-[rgb(52,64,84)] text-xs font-semibold rounded bg-gray-100">
              {t("modal_actors.actor_card.hd")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { Card };
